package com.nonopn.skywatcher.maps.providers

import com.nonopn.skywatcher.app.utils.RootPath
import com.nonopn.skywatcher.maps.CacheManager
import eu.codlab.files.VirtualFile
import eu.codlab.http.Configuration
import eu.codlab.http.createClient
import io.ktor.client.request.get
import io.ktor.client.statement.bodyAsChannel
import io.ktor.http.isSuccess
import io.ktor.utils.io.ByteReadChannel
import io.ktor.utils.io.exhausted
import io.ktor.utils.io.toByteArray
import kotlinx.io.Buffer
import kotlinx.io.RawSource

abstract class AbstractMapProvider(
    cacheDirName: String,
    private val useFileCache: Boolean = false
) {
    private val cacheFolder = VirtualFile(RootPath, ".lpsmanager/tiles")
    private val client = createClient(
        Configuration(enableLogs = false)
    ) { }
    private val cacheManager = CacheManager(cacheDirName = cacheDirName) { x, y, z ->
        getTileAsChannel(x, y, z).toByteArray()
    }

    suspend fun initialize() {
        cacheFolder.mkdirs()
        cacheManager.initialize()
    }

    suspend fun reset() {
        cacheManager.reset()
    }

    protected abstract suspend fun buildUrl(row: Int, col: Int, zoomLvl: Int): String

    private suspend fun getTileAsByteArray(
        row: Int,
        col: Int,
        zoomLvl: Int,
        writeInFile: VirtualFile? = null
    ): RawSource? {
        val url = buildUrl(row, col, zoomLvl)

        val request = client.get(url)
        if (!request.status.isSuccess()) {
            return null
        }

        return Buffer().also {
            val array = request.bodyAsChannel().toByteArray()

            writeInFile?.let { write -> write.write(array) }
            it.write(array)
        }
    }

    private suspend fun getTileAsChannel(row: Int, col: Int, zoomLvl: Int): ByteReadChannel {
        val url = buildUrl(row, col, zoomLvl)

        val request = client.get(url)
        if (!request.status.isSuccess()) {
            throw IllegalStateException("")
        }

        val body = request.bodyAsChannel()
        body.exhausted()

        return body
    }

    @Suppress("ReturnCount")
    suspend fun getTile(row: Int, col: Int, zoomLvl: Int): RawSource? {
        return try {
            val file = VirtualFile(cacheFolder, "${row}_${col}_$zoomLvl.jpeg")

            if (useFileCache && file.exists()) {
                file.read().takeIf { it.isNotEmpty() }?.let { content ->
                    return Buffer().also { it.write(content) }
                }
            }

            val inCache = cacheManager.getCached(row, col, zoomLvl)

            if (null != inCache) {
                return inCache
            }

            getTileAsByteArray(row, col, zoomLvl, file.takeIf { useFileCache })?.also {
                // cache whenever we have a non-null value
                cacheManager.cacheTile(row, col, zoomLvl)
            }
        } catch (_: Throwable) {
            null
        }
    }
}
